import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import {
    Mail,
    Lock,
    User as UserIcon,
    ArrowRight,
    ShieldCheck,
    ShieldX,
    Loader2,
    Eye,
    EyeOff,
    HeartHandshake,
    ChevronDown,
} from "lucide-react";
import Logo from "../components/Logo";
import PhotoCapture from "../components/PhotoCapture";

// Storage key shared with the Google OAuth callback so the token survives the redirect.
const INVITE_STORAGE_KEY = "shariti_invite_token";

const ROLE_LABELS = {
    admin: "Administrateur·rice",
    manager: "Manager",
    learner: "Apprenant·e",
    candidate: "Candidat·e",
};

/**
 * Iter-32 — OAuth Google direct (sans intermédiaire Emergent).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
async function startGoogleLogin() {
    const redirectUri = `${window.location.origin}/auth/google`;
    try {
        const { data } = await api.get(
            `/auth/google/start?redirect_uri=${encodeURIComponent(redirectUri)}`,
        );
        window.location.href = data.auth_url;
    } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(
            typeof detail === "string"
                ? detail
                : "Impossible de démarrer la connexion Google. Réessaie.",
        );
    }
}

export default function Register() {
    const [searchParams] = useSearchParams();
    const tokenFromUrl = searchParams.get("invite") || "";

    // Token validation state
    const [checking, setChecking] = useState(!!tokenFromUrl);
    const [invite, setInvite] = useState(null); // null until check returns
    const [inviteError, setInviteError] = useState("");

    // Validate invite as soon as the page loads
    useEffect(() => {
        if (!tokenFromUrl) {
            setChecking(false);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(`/invitations/${encodeURIComponent(tokenFromUrl)}`);
                if (data.valid) {
                    setInvite(data);
                    // Stash for Google OAuth flow
                    try {
                        sessionStorage.setItem(INVITE_STORAGE_KEY, tokenFromUrl);
                    } catch (e) { /* ignore */ }
                } else {
                    setInviteError(data.reason || "Invitation invalide.");
                }
            } catch (err) {
                setInviteError(
                    formatApiError(err.response?.data?.detail) || "Invitation introuvable.",
                );
            } finally {
                setChecking(false);
            }
        })();
    }, [tokenFromUrl]);

    if (checking) {
        return (
            <GateScreen
                icon={<Loader2 className="w-10 h-10 text-shariti-orange animate-spin" />}
                title="Vérification..."
                subtitle="On vérifie ton invitation."
            />
        );
    }

    // Token in URL but invalid → gate screen
    if (tokenFromUrl && !invite) {
        return (
            <GateScreen
                icon={<ShieldX className="w-10 h-10 text-red-400" />}
                title="Invitation invalide"
                subtitle={inviteError || "Ce lien d'invitation n'est plus valable."}
                body="Demande un nouveau lien à ton manager ou à un administrateur — ou inscris-toi librement depuis la page d'inscription."
            />
        );
    }

    // Iter-34 — no token → open self-signup ; valid token → invite flow
    return <SignupForm invite={invite} tokenFromUrl={tokenFromUrl} />;
}

function SignupForm({ invite, tokenFromUrl }) {
    const selfMode = !tokenFromUrl;
    const [name, setName] = useState(invite?.name || "");
    const [email, setEmail] = useState(invite?.email || "");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    // Iter-34 — self-signup extras
    const [photo, setPhoto] = useState(null);
    const [associationId, setAssociationId] = useState("");
    const [associations, setAssociations] = useState([]);

    const { register } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!selfMode) return;
        api.get("/public/associations")
            .then(({ data }) => setAssociations(data))
            .catch(() => {});
    }, [selfMode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error("Le mot de passe doit faire au moins 6 caractères");
            return;
        }
        if (selfMode && !associationId) {
            toast.error("Choisis l'association pour laquelle tu as postulé");
            return;
        }
        if (selfMode && !photo) {
            toast.error("Ajoute une photo de toi pour valider ton inscription");
            return;
        }
        setLoading(true);
        try {
            const data = await register(
                email,
                password,
                name,
                tokenFromUrl,
                selfMode ? { photo, association_id: associationId } : {},
            );
            if (data?.role === "candidate") {
                toast.success("Inscription envoyée ! Elle sera validée sous peu.");
                navigate("/candidate-waiting");
            } else {
                toast.success("Compte créé. Let's go !");
                navigate("/onboarding");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignup = () => {
        if (tokenFromUrl) {
            try {
                sessionStorage.setItem(INVITE_STORAGE_KEY, tokenFromUrl);
            } catch (e) { /* ignore */ }
        }
        startGoogleLogin();
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-shariti-deep-black px-6 py-10 relative overflow-hidden">
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-shariti-orange/25 blur-[120px] animate-blob" />
            <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-shariti-yellow/10 blur-[100px] animate-blob" />

            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md relative z-10"
            >
                <div className="mb-8 flex justify-center">
                    <Logo
                        variant="full"
                        showAcademy
                        academySize="md"
                        className="h-20 w-auto drop-shadow-[0_0_24px_rgba(238,114,46,0.35)]"
                    />
                </div>

                {invite && (
                    <div
                        className="glass-card rounded-2xl p-3 mb-6 flex items-start gap-3 border border-shariti-orange/20"
                        data-testid="invite-valid-banner"
                    >
                        <ShieldCheck className="w-5 h-5 text-shariti-orange-light shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <p className="text-white font-bold">Invitation vérifiée</p>
                            <p className="text-white/60 text-xs">
                                Rôle :{" "}
                                <span className="font-bold text-shariti-orange-light">
                                    {ROLE_LABELS[invite.role] || invite.role}
                                </span>
                                {invite.email && (
                                    <span className="block">Email : {invite.email}</span>
                                )}
                            </p>
                        </div>
                    </div>
                )}

                <p className="text-xs font-thin tracking-[0.3em] text-shariti-orange-light uppercase mb-2">
                    Rejoindre l'académie
                </p>
                <h2 className="text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-3">
                    Créer ton compte
                </h2>
                {selfMode && (
                    <p className="text-white/50 font-light text-sm mb-6" data-testid="self-signup-hint">
                        Inscris-toi en 1 minute — ton inscription sera validée par l'équipe
                        Shariti avant l'accès à la formation.
                    </p>
                )}

                <button
                    onClick={handleGoogleSignup}
                    data-testid="register-google-button"
                    className="w-full flex items-center justify-center gap-3 glass-card rounded-2xl py-3.5 mb-2 hover:border-shariti-orange/40 transition-all font-bold text-white"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" opacity="0.9"/>
                        <path fill="#EE722E" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity="0.7"/>
                        <path fill="#FCDB53" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continuer avec Google
                </button>
                {selfMode && (
                    <p className="text-[11px] text-white/40 text-center mb-4">
                        Avec Google, tu complèteras ta photo et ton association juste après.
                    </p>
                )}

                <div className="flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs font-thin tracking-widest text-white/40 uppercase">
                        ou avec ton email
                    </span>
                    <div className="flex-1 h-px bg-white/10" />
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
                    <div className="relative">
                        <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                            type="text"
                            placeholder="Ton prénom et nom"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            data-testid="register-name-input"
                            className="w-full pl-12 pr-4 py-3.5 glass-card rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-shariti-orange/60"
                        />
                    </div>
                    <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                            type="email"
                            placeholder="ton@email.fr"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            readOnly={!!invite?.email}
                            data-testid="register-email-input"
                            className={`w-full pl-12 pr-4 py-3.5 glass-card rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-shariti-orange/60 ${
                                invite?.email ? "opacity-80 cursor-not-allowed" : ""
                            }`}
                        />
                    </div>
                    <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                            type={showPwd ? "text" : "password"}
                            placeholder="6 caractères minimum"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            data-testid="register-password-input"
                            className="w-full pl-12 pr-12 py-3.5 glass-card rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-shariti-orange/60"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPwd((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-shariti-orange/15 text-shariti-orange hover:bg-shariti-orange/25 transition-colors"
                            data-testid="register-password-toggle"
                            aria-label={
                                showPwd
                                    ? "Masquer le mot de passe"
                                    : "Afficher le mot de passe"
                            }
                        >
                            {showPwd ? (
                                <EyeOff className="w-4 h-4" />
                            ) : (
                                <Eye className="w-4 h-4" />
                            )}
                        </button>
                    </div>

                    {selfMode && (
                        <>
                            <div className="relative">
                                <HeartHandshake className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
                                <select
                                    value={associationId}
                                    onChange={(e) => setAssociationId(e.target.value)}
                                    required
                                    data-testid="register-association-select"
                                    className={`w-full pl-12 pr-10 py-3.5 glass-card rounded-2xl bg-transparent appearance-none focus:outline-none focus:border-shariti-orange/60 ${
                                        associationId ? "text-white" : "text-white/30"
                                    }`}
                                >
                                    <option value="" className="bg-shariti-deep-black text-white/60">
                                        L'association pour laquelle tu as postulé…
                                    </option>
                                    {associations.map((a) => (
                                        <option
                                            key={a.association_id}
                                            value={a.association_id}
                                            className="bg-shariti-deep-black text-white"
                                        >
                                            {a.name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                            </div>
                            <PhotoCapture value={photo} onChange={setPhoto} />
                        </>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="register-submit-button"
                        className="w-full flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                    >
                        {loading
                            ? "Création..."
                            : selfMode
                              ? "Envoyer mon inscription"
                              : "Créer mon compte"}
                        {!loading && <ArrowRight className="w-5 h-5" />}
                    </button>
                </form>

                <p className="text-center text-sm text-white/50 mt-6">
                    Déjà inscrit ?{" "}
                    <Link to="/login" className="text-shariti-orange font-bold">
                        Connexion
                    </Link>
                </p>
            </motion.div>
        </div>
    );
}

function GateScreen({ icon, title, subtitle, body }) {
    return (
        <div
            className="min-h-screen flex items-center justify-center bg-shariti-deep-black px-6 py-10 relative overflow-hidden"
            data-testid="invite-gate-screen"
        >
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-shariti-orange/20 blur-[120px] animate-blob" />
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md relative z-10 text-center"
            >
                <div className="mb-6 flex justify-center">
                    <Logo
                        variant="full"
                        showAcademy
                        academySize="md"
                        className="h-20 w-auto drop-shadow-[0_0_24px_rgba(238,114,46,0.35)]"
                    />
                </div>
                <div className="glass-card rounded-3xl p-8">
                    <div className="flex justify-center mb-4">{icon}</div>
                    <h1 className="text-2xl font-extrabold text-white mb-2">{title}</h1>
                    <p className="text-white/70 mb-4">{subtitle}</p>
                    {body && <p className="text-sm text-white/50">{body}</p>}
                    <div className="flex flex-col items-center gap-3 mt-6">
                        <Link
                            to="/register"
                            className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                        >
                            S'inscrire librement
                        </Link>
                        <Link
                            to="/login"
                            className="text-sm text-white/50 hover:text-white underline underline-offset-4"
                        >
                            Retour à la connexion
                        </Link>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
